import { DefaultMap } from '@seaber/common/lib/collections';
import { toM3 } from '../../constants/VolumeUnits';
import {
  type VesselSpeedType,
  type VesselArea,
  type VesselSpeedConsumptionFragment,
  type VesselDraftDatapointFragment,
  type VesselSpeedTag,
  type ScenarioTimeline_ShipFragment,
  WeightUnits,
} from '../../graphql';
import { getShipWeightCapacity } from './Core';
import { omit } from 'lodash';

export type VSCSByTypeAndArea = Map<
  VesselSpeedType,
  Map<VesselArea | null, VesselSpeedConsumptionFragment[]>
>;

export type VSCSByTagTypeAndArea = Map<
  VesselSpeedTag | null,
  VSCSByTypeAndArea
>;

export type Vessel = ScenarioTimeline_ShipFragment & {
  speedConsumptions: VSCSByTypeAndArea;
  speedConsumptionsByTag: VSCSByTagTypeAndArea;
  draftDatapoints: VesselDraftDatapointFragment[];
};
export const defaultVessel: Vessel = {
  vesselAttributesAssociationsByShipId: { nodes: [] },
  vesselSpeedConsumptionsByShipId: { nodes: [] },
  cargoCompartmentsByShipId: { nodes: [] },
  id: NaN,
  volumeCapacity: 100000,
  weightCapacity: 100000,
  massCapacity: 100000,
  inactive: false,
  speedConsumptions: new DefaultMap(() => new DefaultMap(() => [])),
  speedConsumptionsByTag: new Map(),
  shipVoyageNamingRuleRelationsByShipId: { nodes: [] },
  tcContractsByVesselId: { nodes: [] },
  vesselDraftDatapointsByShipId: { nodes: [] },
  draftDatapoints: [],
  vesselFulfillsRequirementAssociationsByVesselId: { nodes: [] },
};

export type NormalizedVessel = Omit<
  Vessel,
  'massCapacity' | 'volumeCapacity' | 'massUnit' | 'volumeUnit'
> & {
  capacity: {
    mass: number;
    volume: number;
  };
};

export function normalizeVessel(vessel: Vessel): NormalizedVessel {
  const { massCapacity, volumeCapacity } = getVesselCapacities(vessel);
  return {
    ...omit(vessel, [
      'massCapacity',
      'volumeCapacity',
      'massUnit',
      'volumeUnit',
      'weightCapacityType',
    ]),
    capacity: {
      mass: massCapacity,
      volume: volumeCapacity,
    },
  };
}

/** TODO fix units for mass and volume capacity
 * TODO: might be best to change return type to (number | null) unconditionally to force handling missing capacity values on case-by-case basis. the default value has been leaking to various places with unpleasant implicit effects. */
export function getVesselCapacities(
  vessel: ScenarioTimeline_ShipFragment | null,
  defaultValue?: number,
): {
  massCapacity: number;
  massCapacitySource: 'DWCC' | 'DWT' | 'fallback';
  volumeCapacity: number;
};
export function getVesselCapacities(
  vessel: ScenarioTimeline_ShipFragment | null,
  defaultValue: null,
): {
  massCapacity: number | null;
  massCapacitySource: 'DWCC' | 'DWT' | null;
  volumeCapacity: number | null;
};
export function getVesselCapacities(
  vessel: ScenarioTimeline_ShipFragment | null,
  defaultValue: number | null = 1000000,
): {
  massCapacity: number | null;
  massCapacitySource: 'DWCC' | 'DWT' | 'fallback' | null;
  volumeCapacity: number | null;
} {
  const massCapacity = vessel?.massCapacity
    ? getShipWeightCapacity(
        +vessel.massCapacity,
        vessel.massUnit ?? WeightUnits.TONNE,
      )
    : (vessel?.weightCapacity ?? defaultValue);

  const massCapacitySource = vessel?.massCapacity
    ? 'DWCC'
    : vessel?.weightCapacity
      ? 'DWT'
      : 'fallback';

  const volumeCapacity =
    vessel?.volumeCapacity && vessel.volumeUnit
      ? toM3(+vessel.volumeCapacity, vessel.volumeUnit)
      : defaultValue;

  return {
    massCapacity,
    massCapacitySource,
    volumeCapacity,
  };
}
